import { useState, useEffect, createElement } from 'react';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { AttachField } from '@promo-platform/attach-field';
import { CheckboxField } from '@promo-platform/checkbox-field';
import { DateField } from '@promo-platform/date-field';
import { ErrorMessage } from '@promo-platform/error-message';
import { IntegerField } from '@promo-platform/integer-field';
import { LdsField } from '@promo-platform/lds-field';
import { LongTextField } from '@promo-platform/long-text-field';
import { SelectField } from '@promo-platform/select-field';
import { SelectListField } from '@promo-platform/select-list';
import { ShortTextField } from '@promo-platform/short-text-field';
import { Skeleton, SkeletonTypes } from '@promo-platform/skeleton';
import { SkeletonBlock } from '@promo-platform/skeleton-block';
import { GoogleReCaptchaProvider, GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useForm, FormProvider } from 'react-hook-form';
import { QueryClient, QueryClientProvider, useQuery } from 'react-query';
import axios from 'axios';
import { getQueries } from '@promo-platform/utils';
import { isBefore, isAfter } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

function __awaiter(thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }

  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }

    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }

    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }

    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
}

const getFormData = (url, id) => __awaiter(void 0, void 0, void 0, function* () {
    const { data } = yield axios.get(`${url}/${id}`);
    return data;
});

const sendFormData = ({ url, id, body, token }) => __awaiter(void 0, void 0, void 0, function* () {
    const { data } = yield axios.post(`${url}/${id}/response`, {
        responses: body,
        captchaToken: token || null,
    });
    return data;
});

var FORM;
(function (FORM) {
    FORM["FORM_ID"] = "form-creator-form";
    FORM["ID"] = "id";
    FORM["NAME_DESCRIPTION_CARD_ID"] = "form-name-description-card";
    FORM["RESULT_CARD_ID"] = "form-result-card";
    FORM["FIELD_ARRAY_ID"] = "fieldArrayId";
    FORM["TITLE"] = "title";
    FORM["DESCRIPTION"] = "description";
    FORM["START_DATE_TIME_SETTINGS"] = "startDateSettings";
    FORM["END_DATE_TIME_SETTINGS"] = "endDateSettings";
    FORM["DATE_TIME"] = "localDateTime";
    FORM["REDIRECT_URL"] = "redirectUrl";
    FORM["TIME_ZONE"] = "timeZoneInfo";
    FORM["SUBMIT_BUTTON_TEXT"] = "submitButtonText";
    FORM["SUBMIT_BUTTON_ARIA_LABEL"] = "submitButtonDescription";
    FORM["BACK_BUTTON_TEXT"] = "backButtonText";
    FORM["BACK_BUTTON_ARIA_LABEL"] = "backButtonDescription";
    FORM["THANK_YOU_PAGE_URL"] = "thankYouPageUrl";
    FORM["LABEL"] = "label";
    FORM["ALIAS"] = "alias";
    FORM["TYPE"] = "type";
    FORM["TOOLTIP_TEXT"] = "tooltip";
    FORM["TOOLTIP_LABEL"] = "tooltipLabel";
    FORM["SECTIONS"] = "sections";
    FORM["FIELDS"] = "fields";
    FORM["PLACEHOLDER"] = "placeholder";
    FORM["ERROR_MESSAGE"] = "errorMessage";
    FORM["VALUE"] = "value";
    FORM["IS_SELECTED"] = "isSelected";
    FORM["MANDATORY_VALIDATION"] = "mandatoryValidation";
    FORM["MIN_LENGTH_VALIDATION"] = "minLengthValidation";
    FORM["MAX_LENGTH_VALIDATION"] = "maxLengthValidation";
    FORM["MAX_FILE_SIZE_VALIDATION"] = "maxFileSizeValidation";
    FORM["MAX_NUMBER_OF_ANSWERS_VALIDATION"] = "maxNumberOfAnswersValidation";
    FORM["MIN_NUMBER_OF_ANSWERS_VALIDATION"] = "minNumberOfAnswersValidation";
    FORM["PATTERN_VALIDATION"] = "patternValidation";
    FORM["ACCEPTED_FILE_TYPES_VALIDATION"] = "acceptedFileTypesValidation";
    FORM["ACCEPTED_FILE_TYPES"] = "acceptedFileTypes";
    FORM["LIST_OF_VALUES"] = "listOfValues";
    FORM["NUMBER_OF_ROWS"] = "numberOfRows";
    FORM["LEGAL_DOCUMENT_SOURCE_TYPE"] = "legalDocumentSourceType";
    FORM["DEFAULT_VALUE"] = "defaultValue";
    FORM["RECAPTCHA_SITE_KEY"] = "recaptchaSiteKey";
    FORM["IS_HIDDEN"] = "isHidden";
    FORM["CONDITIONS"] = "conditions";
})(FORM || (FORM = {}));
const DEFAULT_ERROR_MESSAGE = 'Oops… something went wrong, please try again later.';

var FORM_TYPES;
(function (FORM_TYPES) {
    FORM_TYPES["ShortTextField"] = "ShortTextField";
    FORM_TYPES["LongTextField"] = "LongTextField";
    FORM_TYPES["BooleanField"] = "BooleanField";
    FORM_TYPES["SelectListField"] = "SelectListField";
    FORM_TYPES["SelectField"] = "SelectField";
    FORM_TYPES["AttachFileField"] = "AttachFileField";
    FORM_TYPES["LegalField"] = "LegalField";
    FORM_TYPES["DateField"] = "DateField";
    FORM_TYPES["IntegerField"] = "IntegerField";
})(FORM_TYPES || (FORM_TYPES = {}));

const SeparatorIcon = () => (jsx("svg", Object.assign({ xmlns: "http://www.w3.org/2000/svg", width: "30.828", height: "54.828", viewBox: "0 0 30.828 54.828" }, { children: jsxs("g", Object.assign({ id: "Group_1175", "data-name": "Group 1175", transform: "translate(-191.586 -382.336)" }, { children: [jsxs("g", Object.assign({ id: "Group_1143", "data-name": "Group 1143", transform: "translate(-469.5 -269.75)" }, { children: [jsx("line", { id: "Line_32", "data-name": "Line 32", x2: "14", y2: "14", transform: "translate(662.5 653.5)", fill: "none", stroke: "#707070", strokeLinecap: "round", strokeWidth: "2" }), jsx("line", { id: "Line_33", "data-name": "Line 33", y1: "14", x2: "14", transform: "translate(676.5 653.5)", fill: "none", stroke: "#707070", strokeLinecap: "round", strokeWidth: "2" })] })), jsxs("g", Object.assign({ id: "Group_1144", "data-name": "Group 1144", transform: "translate(-469.5 -264.75)" }, { children: [jsx("line", { id: "Line_35", "data-name": "Line 35", x2: "14", y2: "14", transform: "translate(662.5 667.5)", fill: "none", stroke: "#d4d4d4", strokeLinecap: "round", strokeWidth: "2" }), jsx("line", { id: "Line_34", "data-name": "Line 34", y1: "14", x2: "14", transform: "translate(676.5 667.5)", fill: "none", stroke: "#d4d4d4", strokeLinecap: "round", strokeWidth: "2" })] })), jsxs("g", Object.assign({ id: "Group_1145", "data-name": "Group 1145", transform: "translate(-469.5 -259.75)" }, { children: [jsx("line", { id: "Line_37", "data-name": "Line 37", x2: "14", y2: "14", transform: "translate(662.5 681.5)", fill: "none", stroke: "#f2f2f2", strokeLinecap: "round", strokeWidth: "2" }), jsx("line", { id: "Line_36", "data-name": "Line 36", y1: "14", x2: "14", transform: "translate(676.5 681.5)", fill: "none", stroke: "#f2f2f2", strokeLinecap: "round", strokeWidth: "2" })] }))] })) })));

const prepareLegalBody = ({ formData, field, queryClient }) => {
    const queries = getQueries(field.description);
    const ldsArray = queries
        .map((lds) => {
        const data = queryClient.getQueryData(lds.data.legalDocumentId);
        if (!data) {
            return null;
        }
        return {
            documentId: data.documentId,
            revisionId: data.revisionId,
        };
    })
        .filter((item) => item);
    return {
        type: field.type,
        id: field.id,
        value: formData[field.alias],
        legalDocuments: ldsArray.length > 0 ? ldsArray : null,
    };
};
const prepareBody = (requestData, formData, queryClient) => {
    const body = requestData.sections.map(({ fields }) => fields
        .filter((field) => !field.conditions.length || field.alias in formData)
        .map((field) => {
        var _a, _b;
        if (field.type === FORM_TYPES.AttachFileField) {
            const fileValue = ((_a = formData[field.alias]) === null || _a === void 0 ? void 0 : _a.length) > 0
                ? formData[field.alias].map((file) => file.value)
                : null;
            return {
                type: field.type,
                id: field.id,
                value: fileValue,
            };
        }
        if (field.type === FORM_TYPES.LegalField) {
            return prepareLegalBody({ formData, field, queryClient });
        }
        if (field.type === FORM_TYPES.SelectListField) {
            return {
                type: field.type,
                id: field.id,
                value: typeof formData[field.alias] === 'string'
                    ? [formData[field.alias]]
                    : formData[field.alias],
            };
        }
        if (field.type === FORM_TYPES.BooleanField) {
            return {
                type: field.type,
                id: field.id,
                value: formData[field.alias],
            };
        }
        return {
            type: field.type,
            id: field.id,
            value: ((_b = formData[field.alias]) === null || _b === void 0 ? void 0 : _b.value) || formData[field.alias] || null,
        };
    }));
    return body.flat();
};
const findUtmParemeter = (label, utmParameters) => utmParameters === null || utmParameters === void 0 ? void 0 : utmParameters.find(({ name }) => name === label);
const mapApiResponse = ({ apiProps, settings, utmParameters, }) => {
    var _a;
    switch (apiProps.type) {
        case FORM_TYPES.SelectListField:
        case FORM_TYPES.SelectField:
            return Object.assign(Object.assign({}, apiProps), { options: apiProps.listOfValues.map(({ alias, value, isSelected }) => ({
                    value: alias,
                    label: value,
                    isSelected,
                })) });
        case FORM_TYPES.LegalField:
            return Object.assign(Object.assign({}, apiProps), { config: settings === null || settings === void 0 ? void 0 : settings.config });
        case FORM_TYPES.ShortTextField:
            return Object.assign(Object.assign({}, apiProps), { defaultValue: (apiProps.isHidden &&
                    ((_a = findUtmParemeter(apiProps.label, utmParameters)) === null || _a === void 0 ? void 0 : _a.value)) ||
                    apiProps.defaultValue });
        default:
            return apiProps;
    }
};
const redirectFunction = (url, callback) => {
    if (callback) {
        callback(url);
    }
    else {
        window.open(url, '_self');
    }
};
const isFormInactive = (startDateSettings, endDateSettings, callback) => {
    if (startDateSettings) {
        const newDate = utcToZonedTime(new Date(), startDateSettings.timeZoneInfo);
        const utcDate = utcToZonedTime(startDateSettings.localDateTime, startDateSettings.timeZoneInfo);
        if (isBefore(newDate, utcDate)) {
            redirectFunction(startDateSettings.redirectUrl, callback);
        }
    }
    if (endDateSettings) {
        const newDate = utcToZonedTime(new Date(), endDateSettings.timeZoneInfo);
        const utcDate = utcToZonedTime(endDateSettings.localDateTime, endDateSettings.timeZoneInfo);
        if (isAfter(newDate, utcDate)) {
            redirectFunction(endDateSettings.redirectUrl, callback);
        }
    }
};

const queryClient = new QueryClient();
const PromoForm = ({ url, settings, id, onSuccessCallback, onRedirectCallback, utmParameters, data, }) => {
    const [token, setToken] = useState('');
    const [formError, setFormError] = useState('');
    const [currentStep, setCurrentStep] = useState(0);
    const handleNext = () => {
        if (!data)
            return;
        if (currentStep < data.sections.length - 1) {
            setTimeout(() => {
                setCurrentStep(currentStep + 1);
            }, 200);
        }
    };
    const handlePrevious = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };
    const defaultValues = data.sections.reduce((acc1, { fields }) => (Object.assign(Object.assign({}, acc1), fields.reduce((acc2, field) => {
        const ret = acc2;
        if ('defaultValue' in field)
            ret[field.alias] = field.defaultValue;
        if (field.type === FORM_TYPES.AttachFileField)
            ret[field.alias] = [];
        return ret;
    }, {}))), {});
    const methods = useForm({ defaultValues });
    const componentsMap = Object.assign({ ShortTextField: ShortTextField, LongTextField: LongTextField, BooleanField: CheckboxField, SelectListField: SelectListField, SelectField: SelectField, AttachFileField: AttachField, LegalField: LdsField, IntegerField: IntegerField, DateField: DateField }, ((settings === null || settings === void 0 ? void 0 : settings.components) || {}));
    useEffect(() => {
        if (data) {
            isFormInactive(data.startDateSettings, data.endDateSettings, onRedirectCallback);
        }
    }, [data]);
    const onSuccess = (redirectUrl) => {
        if (onSuccessCallback) {
            onSuccessCallback(redirectUrl);
        }
        else {
            window.open(redirectUrl, '_self');
        }
    };
    const handleToken = (value) => {
        if (!token) {
            setToken(value);
        }
    };
    const onSubmit = (values) => __awaiter(void 0, void 0, void 0, function* () {
        if (data) {
            const body = prepareBody(data, values, queryClient);
            try {
                yield sendFormData({ url, id, body, token });
                onSuccess(data.thankYouPageUrl);
            }
            catch (_a) {
                setFormError(DEFAULT_ERROR_MESSAGE);
            }
        }
    });
    return (jsx("div", Object.assign({ className: "pp-form__container" }, { children: jsxs(GoogleReCaptchaProvider, Object.assign({ reCaptchaKey: (data === null || data === void 0 ? void 0 : data.recaptchaSiteKey) || '' }, { children: [jsx(FormProvider, Object.assign({}, methods, { children: jsxs("form", Object.assign({ onSubmit: methods.handleSubmit(onSubmit), className: "pp-form" }, { children: [jsx("h2", Object.assign({ className: "pp-form__title" }, { children: data.title })), jsx("p", Object.assign({ className: "pp-form__description" }, { children: data.description })), jsx("div", Object.assign({ className: "pp-form__separator" }, { children: jsx(SeparatorIcon, {}) })), (data === null || data === void 0 ? void 0 : data.sections) && data.sections[currentStep] ? (jsxs(Fragment, { children: [data.sections[currentStep].fields.map((props) => {
                                        const Component = componentsMap[props.type];
                                        const preparedProps = mapApiResponse({
                                            apiProps: props,
                                            settings,
                                            utmParameters,
                                        });
                                        return Component ? (createElement(Component, Object.assign({}, preparedProps, { key: props.id }))) : null;
                                    }), data.sections.length > 1 ? (jsxs("div", Object.assign({ className: "pp-form__section-button-container" }, { children: [data.sections[currentStep].backButtonText ? (jsx("button", Object.assign({ type: "button", className: "pp-form__section-button", onClick: handlePrevious, "aria-label": data.sections[currentStep].backButtonDescription ||
                                                    data.sections[currentStep].backButtonText, disabled: currentStep === 0 }, { children: data.sections[currentStep].backButtonText }))) : null, data.sections[currentStep].submitButtonText ? (jsx("button", Object.assign({ onClick: handleNext, type: currentStep === data.sections.length - 1
                                                    ? 'submit'
                                                    : 'button', className: "pp-form__section-button", "aria-label": data.sections[currentStep].submitButtonDescription ||
                                                    data.sections[currentStep].submitButtonText }, { children: data.sections[currentStep].submitButtonText }))) : null] }))) : null] })) : null, jsx(ErrorMessage, { message: formError }), data.sections.length === 1 ? (jsx("button", Object.assign({ type: "submit", className: "pp-form__submit-button", disabled: methods.formState.isSubmitting, "aria-label": data.submitButtonDescription || data.submitButtonText }, { children: data.submitButtonText }))) : null] })) })), jsx(GoogleReCaptcha, { action: id.replace(/-/g, ''), onVerify: handleToken })] })) })));
};
const PromoFormSkeleton = () => (jsx("div", Object.assign({ className: "pp-form__container" }, { children: jsxs("form", Object.assign({ className: "pp-form" }, { children: [jsx("h2", Object.assign({ className: "pp-form__title" }, { children: jsx(Skeleton, { type: SkeletonTypes.Title }) })), jsx("div", Object.assign({ className: "pp-form__description" }, { children: jsx(Skeleton, { type: SkeletonTypes.Text, lines: 2 }) })), jsx("div", Object.assign({ className: "pp-form__separator" }, { children: jsx(SeparatorIcon, {}) })), jsx(SkeletonBlock, { amount: 4 }), jsx(Skeleton, { type: SkeletonTypes.Title })] })) })));
const PromoFormWrap = ({ url, settings, id, onSuccessCallback, onRedirectCallback, utmParameters, }) => {
    const { data } = useQuery('promoData', () => getFormData(url, id));
    return data ? (jsx(PromoForm, { url: url, settings: settings, id: id, utmParameters: utmParameters, onSuccessCallback: onSuccessCallback, onRedirectCallback: onRedirectCallback, data: data })) : (jsx(PromoFormSkeleton, {}));
};
const PromoFormApp = ({ url, settings, id, utmParameters, onSuccessCallback, onRedirectCallback, }) => (jsx(QueryClientProvider, Object.assign({ client: queryClient }, { children: jsx(PromoFormWrap, { url: url, settings: settings, id: id, utmParameters: utmParameters, onSuccessCallback: onSuccessCallback, onRedirectCallback: onRedirectCallback }) })));

export { PromoFormApp as default };
